import React, { useState } from 'react';
import { ChevronDown, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface GoogleCalendarHelpModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Explicación completa de la sincronización con Google Calendar (habilitada para todo el equipo) — se abre
 * desde el ícono (i) al lado del título "Google Calendar" en Perfil. Mismo patrón visual que
 * `CalendarHelpModal` ("Cómo funciona tu agenda"). Contenido pensado para que no queden
 * ambigüedades sobre qué hace y, sobre todo, qué NO hace — el caso real que motivó esto es
 * que un evento externo (una reunión, un cumpleaños) en el Google Calendar de la psicóloga
 * NO ocupa ni crea nada en Psi Platform, y eso no es obvio si no se explica.
 */
export const GoogleCalendarHelpModal: React.FC<GoogleCalendarHelpModalProps> = ({
  open,
  onClose,
}) => (
  <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
    <DialogContent className="sm:max-w-[34rem] p-0 overflow-hidden">
      <DialogHeader className="px-6 pt-6 pb-4 mb-0 border-b border-gray-100">
        <DialogTitle className="text-[1.0625rem] font-semibold text-[#4a3d73]">
          Cómo funciona la sincronización con Google Calendar
        </DialogTitle>
        <p className="text-[0.8125rem] text-gray-500 mt-1 leading-snug">
          Qué se sincroniza, cada cuánto, y qué queda exclusivamente en Psi Platform.
        </p>
      </DialogHeader>

      <div className="px-6 py-5 space-y-5 overflow-y-auto max-h-[65vh]">
        <p className="text-[0.8125rem] text-gray-600 leading-relaxed text-justify">
          Cuando conectás tu Google Calendar, cada sesión que agendás, reprogramás o cancelás
          en Psi Platform se refleja automáticamente como un evento en tu calendario personal.
          La sincronización corre sola en segundo plano, aproximadamente cada{' '}
          <strong>12 minutos</strong> — no es instantánea.
        </p>

        <div className="rounded-lg bg-[#f2effa] border border-[#e0d9f5] px-4 py-3">
          <p className="text-[0.8125rem] text-[#4a3d73] leading-relaxed text-justify">
            <strong>Recomendación:</strong> Google Calendar es un espejo cómodo para verlo
            junto al resto de tu agenda personal, pero el lugar donde tenés que agendar,
            reprogramar o cancelar sesiones es <strong>siempre Psi Platform</strong>. Es la
            única forma de garantizar que el paciente, los pagos y el resto del sistema
            queden al día.
          </p>
        </div>

        <section>
          <p className="text-[0.6875rem] font-semibold text-gray-500 uppercase tracking-wider mb-2.5">
            Qué SÍ hace
          </p>
          <ul className="space-y-2.5">
            <HelpItem>
              Sesión nueva agendada en Psi Platform → aparece como evento en tu Google
              Calendar.
            </HelpItem>
            <HelpItem>
              Sesión reprogramada en Psi Platform → el evento en Google se mueve al nuevo
              horario (no se duplica).
            </HelpItem>
            <HelpItem>
              Sesión cancelada en Psi Platform → el evento se borra de tu Google Calendar.
            </HelpItem>
            <HelpItem>
              Si <strong>vos borrás</strong> directamente en Google el evento de una sesión →
              esa sesión se cancela sola en Psi Platform.
            </HelpItem>
            <HelpItem>
              Si <strong>vos movés el horario</strong> directamente en Google del evento de
              una sesión → esa sesión se reprograma sola en Psi Platform, al nuevo horario.
            </HelpItem>
          </ul>
        </section>

        <section>
          <p className="text-[0.6875rem] font-semibold text-gray-500 uppercase tracking-wider mb-2.5">
            Qué NO hace
          </p>
          <ul className="space-y-2.5">
            <HelpItem negative>
              Un evento que vos cargás en Google Calendar por tu cuenta (una reunión, un
              cumpleaños, un turno médico) <strong>no bloquea tu agenda</strong> en Psi
              Platform ni genera ninguna sesión nueva — Google Calendar nunca le &quot;avisa&quot; nada
              a Psi Platform sobre eventos que no creamos nosotros.
            </HelpItem>
            <HelpItem negative>
              No es instantánea: un cambio puede tardar hasta 12 minutos en reflejarse del
              otro lado.
            </HelpItem>
            <HelpItem negative>
              Cambiar solo la descripción, el color o los invitados de un evento (sin tocar
              el horario) no tiene ningún efecto sobre la sesión real.
            </HelpItem>
            <HelpItem negative>
              Si desconectás la sincronización, los eventos que ya se crearon{' '}
              <strong>no se borran</strong> de tu Google Calendar — pero los cambios que
              hagas después en Psi Platform van a dejar de reflejarse ahí hasta que
              reconectes.
            </HelpItem>
          </ul>
        </section>

        <FaqSection />
      </div>

      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
        <Button type="button" variant="primary" size="sm" onClick={onClose}>
          Entendido
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

const HelpItem: React.FC<{ children: React.ReactNode; negative?: boolean }> = ({
  children,
  negative = false,
}) => (
  <li className="flex gap-2.5 items-start">
    {negative ? (
      <XCircle size={16} className="text-gray-400 mt-0.5 shrink-0" />
    ) : (
      <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" />
    )}
    <p className="text-[0.8125rem] text-gray-600 leading-relaxed text-justify">{children}</p>
  </li>
);

interface FaqItem {
  question: string;
  answer: React.ReactNode;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    question: '¿Qué pasa si borro por error el evento de una sesión en Google Calendar?',
    answer:
      'La sesión se cancela automáticamente en Psi Platform en el próximo ciclo de sincronización (hasta 12 min). Es una acción real de cancelación — no una simulación — así que solo borrá el evento si realmente querés cancelar esa sesión.',
  },
  {
    question: '¿Y si en vez de borrarlo, muevo el evento a otro horario desde Google Calendar?',
    answer:
      'La sesión se reprograma automáticamente en Psi Platform al horario nuevo. Igual que la cancelación, es un cambio real: se avisa como cualquier reprogramación hecha desde la plataforma.',
  },
  {
    question: 'Tengo una reunión personal cargada en mi Google Calendar a la misma hora que una sesión, ¿hay conflicto?',
    answer:
      'No. Los eventos que vos cargás por tu cuenta en Google Calendar son completamente invisibles para Psi Platform — no bloquean horarios, no cancelan nada y no generan avisos. La sincronización solo actúa sobre los eventos que Psi Platform mismo creó.',
  },
  {
    question: '¿Qué pasa con los eventos ya creados en Google Calendar si desconecto la sincronización desde Psi Platform?',
    answer:
      'Quedan tal cual en tu Google Calendar — no se borran. Lo único que cambia es que, de ahí en más, lo que hagas en Psi Platform (agendar, reprogramar, cancelar) no se va a reflejar en Google hasta que vuelvas a conectar.',
  },
  {
    question: '¿Si reconecto la sincronización después de haber estado desconectada, se duplican los eventos en Google Calendar?',
    answer:
      'No. Al reconectar, el sistema reconoce qué sesiones ya tenían un evento creado antes de la desconexión y solo actualiza lo que cambió mientras tanto (por ejemplo, si canceleaste o reprogramaste alguna sesión durante ese período) — nunca crea un evento repetido para una sesión que ya estaba sincronizada.',
  },
  {
    question: '¿Por qué a veces tarda unos minutos en aparecer un cambio en Google Calendar?',
    answer:
      'La sincronización no es en tiempo real: corre en segundo plano cada 12 minutos aproximadamente. Es intencional — así evitamos golpear la API de Google constantemente. Si necesitás que un cambio se refleje sí o sí de inmediato, hacé el cambio directo en Psi Platform y esperá el próximo ciclo.',
  },
];

const FaqSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="border-t border-gray-100 pt-4">
      <p className="text-[0.6875rem] font-semibold text-gray-500 uppercase tracking-wider mb-2.5">
        Preguntas frecuentes
      </p>
      <div className="rounded-lg border border-gray-100 divide-y divide-gray-100 overflow-hidden">
        {FAQ_ITEMS.map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div key={item.question}>
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left bg-white hover:bg-gray-50 transition-colors"
              >
                <span className="text-[0.8125rem] font-medium text-gray-700">
                  {item.question}
                </span>
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isOpen && (
                <div className="px-3.5 py-3 bg-[#f7f5fc]">
                  <p className="text-[0.8125rem] text-gray-600 leading-relaxed text-justify">
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default GoogleCalendarHelpModal;
